import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import {
    FunnelStage,
    FUNNEL_STAGES,
    FIYRPOD_DARK,
    FIYRPOD_GREEN,
    FIYRPOD_MUTED,
} from '../../salesFunnel.types';

interface FunnelPipelineProps {
    stageCounts: Record<FunnelStage, number>;
    selectedStage?: FunnelStage | null;
    onStageClick: (stage: FunnelStage | null) => void;
}

const FunnelPipeline: React.FC<FunnelPipelineProps> = ({
    stageCounts,
    selectedStage,
    onStageClick,
}) => {
    const totalProspects = Object.values(stageCounts).reduce((sum, count) => sum + count, 0);

    return (
        <Box
            sx={{
                background: `linear-gradient(135deg, ${FIYRPOD_DARK} 0%, #132238 100%)`,
                border: '1px solid #1e3050',
                borderRadius: 2,
                mb: 3,
                overflow: 'hidden',
            }}
        >
            {/* FiyrPod branded header strip */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid #1e3050',
                }}
            >
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: FIYRPOD_GREEN,
                        boxShadow: `0 0 6px ${FIYRPOD_GREEN}`,
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{
                        color: FIYRPOD_GREEN,
                        fontWeight: 800,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        fontSize: '0.65rem',
                    }}
                >
                    Pipeline
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ color: FIYRPOD_MUTED, ml: 'auto', fontSize: '0.65rem' }}
                >
                    {totalProspects} active prospect{totalProspects !== 1 ? 's' : ''} · click a
                    stage to filter
                </Typography>
            </Box>

            {/* Stage bars */}
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                {FUNNEL_STAGES.map((stage, index) => {
                    const count = stageCounts[stage.key] || 0;
                    const isSelected = selectedStage === stage.key;
                    const pct =
                        totalProspects > 0 ? ((count / totalProspects) * 100).toFixed(0) : '0';

                    return (
                        <Tooltip
                            key={stage.key}
                            title={`${stage.description} — click to filter`}
                            arrow
                        >
                            <Box
                                onClick={() => onStageClick(isSelected ? null : stage.key)}
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 2.5,
                                    px: 0.5,
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? stage.color : `${stage.color}30`,
                                    borderRight:
                                        index < FUNNEL_STAGES.length - 1
                                            ? '1px solid rgba(255,255,255,0.05)'
                                            : 'none',
                                    outline: isSelected ? `2px solid ${stage.color}` : 'none',
                                    outlineOffset: '-2px',
                                    transition: 'all 0.18s ease',
                                    '&:hover': {
                                        backgroundColor: `${stage.color}70`,
                                        transform: 'scaleY(1.04)',
                                        zIndex: 1,
                                    },
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.62rem',
                                        textAlign: 'center',
                                        lineHeight: 1.3,
                                        mb: 0.75,
                                        color: isSelected ? '#0d1b2e' : FIYRPOD_MUTED,
                                        letterSpacing: '0.01em',
                                    }}
                                >
                                    {stage.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: '1.5rem',
                                        lineHeight: 1,
                                        color: isSelected ? '#0d1b2e' : stage.color,
                                    }}
                                >
                                    {count}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.62rem',
                                        color: isSelected
                                            ? 'rgba(13,27,46,0.6)'
                                            : 'rgba(255,255,255,0.3)',
                                        mt: 0.5,
                                    }}
                                >
                                    {pct}%
                                </Typography>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
};

export default FunnelPipeline;
